/**
 * Tiered pricing utility for vial products
 * Based on quantity ordered:
 * - 1-9 vials: $1495 per vial
 * - 20-29 vials: $1315 per vial
 * - 30+ vials: $1140 per vial
 */

export const TIERED_PRICING_TIERS = [
  { minQuantity: 1, maxQuantity: 9, pricePerUnit: 1495 },
  { minQuantity: 20, maxQuantity: 29, pricePerUnit: 1315 },
  { minQuantity: 30, maxQuantity: null, pricePerUnit: 1140 },
];

/**
 * Calculate the per-unit price based on quantity using tiered pricing
 * @param {number} quantity - The number of vials ordered
 * @returns {number} The price per unit for the given quantity
 */
export function calculateTieredPrice(quantity) {
  if (quantity <= 0) {
    return TIERED_PRICING_TIERS[0].pricePerUnit; // Default to first tier
  }

  // Iterate through tiers in reverse order (highest to lowest) to find the first matching tier
  // This ensures we match the highest applicable tier first
  for (let i = TIERED_PRICING_TIERS.length - 1; i >= 0; i--) {
    const tier = TIERED_PRICING_TIERS[i];
    if (quantity >= tier.minQuantity) {
      if (tier.maxQuantity === null || quantity <= tier.maxQuantity) {
        return tier.pricePerUnit;
      }
    }
  }

  // If quantity is between tiers (e.g., 10-19), use the base tier (tier 1)
  // This handles quantities that don't fall into any defined tier range
  return TIERED_PRICING_TIERS[0].pricePerUnit;
}

/**
 * Calculate total price for a given quantity using tiered pricing
 * @param {number} quantity - The number of vials ordered
 * @returns {number} The total price for the quantity
 */
export function calculateTieredTotal(quantity) {
  const pricePerUnit = calculateTieredPrice(quantity);
  return pricePerUnit * quantity;
}

/**
 * Get pricing tier information for a given quantity
 * @param {number} quantity - The number of vials ordered
 * @returns {Object} Object with tier information
 */
export function getTierInfo(quantity) {
  const pricePerUnit = calculateTieredPrice(quantity);
  const tier = TIERED_PRICING_TIERS.find(
    t => pricePerUnit === t.pricePerUnit
  ) || TIERED_PRICING_TIERS[0];

  return {
    tier,
    pricePerUnit,
    totalPrice: pricePerUnit * quantity,
  };
}
